import { useEffect, useMemo, useState } from 'react';
import { Package, Plus, Trash2 } from 'lucide-react';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { MultiSelect } from '../../../shared/ui/MultiSelect.js';
import {
  addPart,
  removePart,
  splitEqually,
  splitSummary,
  toCm,
  wholeBay,
  withFreeSpace,
  type SplitPart,
} from '../model/split-bay.js';

/**
 * O'RINNI TAQSIMLASH — "bu o'rinda nima turadi?" degan YAGONA savol.
 *
 * Ilgari bu savolga ikki xil joyda javob berilardi va ikkalasi bir-biriga
 * o'xshamasdi: katak kartochkasida "Ajratilgan mahsulot" degan ro'yxat
 * bor edi, bo'lish oynasida esa yana bir ro'yxat — o'sha mahsulotlar,
 * boshqa yo'l bilan. Omborchi qaysi biri nima qilishini tushunmasdi.
 *
 * Endi bitta oyna va bitta savol. Javob ikki xil bo'lishi mumkin:
 *
 *   BUTUN O'RIN bitta mahsulotga — hech narsa bo'linmaydi, faqat katak
 *   o'sha mahsulotga biriktiriladi. Bu eng ko'p uchraydigan holat va
 *   shuning uchun oyna aynan shundan boshlanadi.
 *
 *   O'RIN BO'LINADI — "bu mahsulotga hammasi kerak emas, 30 sm yetadi"
 *   deyilganda qolgani o'zi ikkinchi o'rin bo'lib ajraladi. Ya'ni
 *   bo'lish alohida amal emas, u taqsimlashning natijasi.
 *
 * TEPADA CHIZMA. Raqamlar ro'yxati o'rinni ko'z oldiga keltirmaydi:
 * "30, 40, 130" degan uchta son bir xil kenglikda ko'rinadi va qaysi
 * biri katta ekani bilinmaydi. Chizma esa haqiqiy nisbatda — 130 sm li
 * o'rin ekranda ham 30 sm likdan to'rt baravar keng.
 */
export interface CellAllocateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Katak kodi — sarlavhada, qaysi o'rin ekani shubhasiz bo'lsin. */
  cellCode: string;
  /** Qator raqami — bo'linish FAQAT shu qatorga tushadi. */
  level: number;
  /** O'rinning kengligi, metr. */
  bayM: number;
  /** O'rinning qatordagi tartib raqami — 1 dan boshlanadi. */
  bayNumber: number;
  /** Katak HOZIR ajratilgan mahsulotlar — oyna shundan boshlanadi. */
  reservedIds?: readonly string[];
  /** Ajratish mumkin bo'lgan mahsulotlar. */
  products: readonly { id: string; code: string; name: string }[];
  onSubmit: (parts: SplitPart[]) => void;
  pending?: boolean;
  /** Server xatosi — oyna yopilmaydi, xabar shu yerda chiqadi. */
  error?: string | null;
}

/** Chizmadagi eng kichik ko'rinadigan ulush — 1 sm li o'rin ham bilinsin. */
const MIN_SHARE = 0.04;

/** Barqaror bo'sh ro'yxat — har chizishda yangi massiv yasamaslik uchun. */
const EMPTY: readonly string[] = [];

export const CellAllocateDialog = ({
  open,
  onClose,
  cellCode,
  level,
  bayM,
  bayNumber,
  reservedIds = EMPTY,
  products,
  onSubmit,
  pending = false,
  error = null,
}: CellAllocateDialogProps) => {
  const bayCm = toCm(bayM);

  /* Oyna har ochilganda katakning HOZIRGI holatidan boshlanadi: butun
     o'rin va unda turgan mahsulot. Oldingi urinishning yarim qolgan
     raqamlarining yangi katakka hech qanday aloqasi yo'q. */
  const [parts, setParts] = useState<SplitPart[]>(() => wholeBay(bayCm, reservedIds));
  /* `reservedIds` massiv — har chizishda YANGI havola bo'lib kelishi
     mumkin va uni bevosita bog'liqlikka qo'yish oynani cheksiz qayta
     boshlab yuborardi. Shuning uchun tarkibi kalitga aylantiriladi. */
  const reservedKey = reservedIds.join(',');
  useEffect(() => {
    if (open) setParts(wholeBay(bayCm, reservedKey === '' ? [] : reservedKey.split(',')));
  }, [open, bayCm, reservedKey]);

  const summary = useMemo(() => splitSummary(parts, bayCm), [parts, bayCm]);
  const final = useMemo(() => withFreeSpace(parts, bayCm), [parts, bayCm]);
  /** Bitta qism butun o'rinni egallasa — bu bo'lish emas, biriktirish. */
  const splits = final.length > 1;

  const patch = (index: number, change: Partial<SplitPart>): void => {
    setParts((current) =>
      current.map((part, i) => (i === index ? { ...part, ...change } : part)),
    );
  };

  /** Chizmadagi yozuv: bitta nom yoki "3 ta mahsulot". */
  const captionOf = (ids: readonly string[]): string | null => {
    if (ids.length === 0) return null;
    if (ids.length > 1) return t('warehouse.allocateManyProducts', { count: ids.length });
    const only = products.find((item) => item.id === ids[0]);
    return only?.name ?? null;
  };

  const totalShare = Math.max(summary.sumCm, bayCm);

  const productOptions = products.map((item) => ({
    value: item.id,
    code: item.code,
    label: item.name,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouse.allocateTitle', { cell: cellCode })}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('warehouse.cancel')}
          </Button>
          <Button disabled={!summary.valid} loading={pending} onClick={() => onSubmit(final)}>
            {splits
              ? t('warehouse.splitSubmit', { count: final.length })
              : t('warehouse.allocateSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error === null ? null : (
          <p
            role="alert"
            className={cn(
              'border-danger/25 bg-danger-soft text-danger rounded-[var(--radius-sm)]',
              'border px-3 py-2 text-sm',
            )}
          >
            {error}
          </p>
        )}

        <p className="text-text-muted text-sm">
          {t('warehouse.allocateIntro', { width: bayCm })}
        </p>

        {/* CHIZMA — haqiqiy nisbatda va MAHSULOT NOMI bilan: o'rin nima
            uchun taqsimlanayotgani shu yerda ko'rinadi. */}
        <div
          aria-hidden
          className="border-border bg-surface-2 flex gap-[3px] rounded-[var(--radius-sm)] border p-[3px]"
        >
          {parts.map((part, index) => {
            const name = captionOf(part.nomenclatureIds);
            return (
              <div
                // Qismning o'zida identifikator yo'q — tartib raqami yagona belgisi.
                key={index}
                style={{ flexGrow: Math.max(part.widthCm / totalShare, MIN_SHARE) }}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-[2px]',
                  'px-1 py-3 text-center',
                  name === null
                    ? 'bg-[var(--color-fill-empty)]'
                    : 'bg-accent-soft border-accent/30 border',
                )}
              >
                <span className="text-text truncate font-mono text-[0.6875rem] tabular-nums">
                  {String(bayNumber + index).padStart(2, '0')}
                </span>
                <span
                  className={cn(
                    'w-full truncate text-[0.625rem]',
                    name === null ? 'text-text-faint' : 'text-accent',
                  )}
                >
                  {name ?? t('warehouse.splitEmptyPart')}
                </span>
                <span className="text-text-muted truncate text-[0.625rem] tabular-nums">
                  {part.widthCm} sm
                </span>
              </div>
            );
          })}

          {/* Taqsimlanmagan joy — chizmada BO'SHLIQ bo'lib ko'rinadi.
              Raqam bilan aytish yetmaydi: ko'z avval chizmaga tushadi. */}
          {summary.freeCm > 0 ? (
            <div
              style={{ flexGrow: Math.max(summary.freeCm / totalShare, MIN_SHARE) }}
              className={cn(
                'border-border text-text-faint flex min-w-0 items-center justify-center',
                'rounded-[2px] border border-dashed px-1 py-3 text-[0.625rem] tabular-nums',
              )}
            >
              {summary.freeCm} sm
            </div>
          ) : null}
        </div>

        {/* MAHSULOT — kenglikdan OLDIN: taqsimlash undan boshlanadi. */}
        <div className="flex flex-col gap-3">
          {parts.map((part, index) => (
            /* `items-start` — ro'yxat ochilganda tanlagich o'sadi va
               pastdan tekislash o'lcham maydonini pastga sudrab ketardi. */
            <div key={index} className="flex items-start gap-2">
              <MultiSelect
                className="flex-1"
                label={
                  parts.length === 1
                    ? t('warehouse.allocateProductLabel')
                    : t('warehouse.splitProductLabel', { index: index + 1 })
                }
                placeholder={t('warehouse.splitProductNone')}
                value={part.nomenclatureIds}
                options={productOptions}
                onChange={(next) => patch(index, { nomenclatureIds: next })}
              />
              <Input
                mono
                type="number"
                min={1}
                step={5}
                className="w-[7.5rem]"
                label={t('warehouse.splitWidthLabel')}
                suffix={t('builder.unitCm')}
                value={String(part.widthCm)}
                onChange={(event) =>
                  patch(index, { widthCm: Math.round(Number(event.target.value)) || 0 })
                }
              />
              <button
                type="button"
                disabled={parts.length <= 1}
                onClick={() => setParts((current) => removePart(current, index))}
                aria-label={t('warehouse.splitRemovePart', { index: index + 1 })}
                className={cn(
                  /* Yorliq qatori (1.5rem) + maydon balandligining yarmiga
                     tushirish: tugmaning o'z yorlig'i yo'q va busiz u
                     yonidagi maydondan yuqorida osilib qolardi. */
                  'mt-[1.75rem] flex size-9 shrink-0 items-center justify-center',
                  'rounded-[var(--radius-sm)] text-text-faint',
                  'transition-[background-color,color] duration-[var(--motion-fast)]',
                  'hover:bg-danger-soft hover:text-danger',
                  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
                  'disabled:hover:text-text-faint',
                )}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={summary.freeCm < 1}
              onClick={() => setParts((current) => addPart(current, bayCm))}
            >
              <Plus size={14} aria-hidden />
              {t('warehouse.splitAddPart')}
            </Button>

            {/* TEZ TANLOV — o'rin teng bo'linadigan holat ham ko'p va
                uchta raqamni qo'lda terish ortiqcha ish bo'lardi. */}
            <span className="text-text-muted ml-auto text-xs">
              {t('warehouse.splitEqualLabel')}
            </span>
            {[2, 3, 4, 5].map((count) => (
              <Button
                key={count}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setParts((current) => splitEqually(current, bayCm, count))}
              >
                {count}
              </Button>
            ))}
          </div>
        </div>

        {/* HISOB — har doim ko'rinadi, xato bo'lgandagina emas. Odam
            raqam terayotganda "yana qancha qoldi" degan savolga javob
            izlaydi, "xato qildimmi" degan savolga emas. */}
        <p
          aria-live="polite"
          className={cn(
            'tabular flex items-center gap-2 text-sm',
            summary.freeCm < 0 ? 'text-danger' : 'text-text-muted',
          )}
        >
          <Package size={14} aria-hidden className="shrink-0" />
          {summary.freeCm < 0
            ? t('warehouse.splitOver', { over: -summary.freeCm })
            : !splits
              ? t('warehouse.allocateWhole', { width: bayCm })
              : summary.freeCm === 0
                ? t('warehouse.splitFull', { count: parts.length })
                : t('warehouse.splitFree', { free: summary.freeCm })}
        </p>

        {/* Ogohlantirish FAQAT bo'linayotgan bo'lsa: bo'linmasa javonga
            umuman tegilmaydi va bu haqda gapirish chalg'itardi. */}
        {splits ? (
          <p className="text-text-faint text-xs">{t('warehouse.splitNote', { level })}</p>
        ) : null}
      </div>
    </Dialog>
  );
};
